import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ContactForm from "../components/ContactForm";
import FaqSection from "../components/FaqSection";
import {
  SakuraFlower,
  CherryBranchImg,
  JapaneseBadge,
} from "../components/SakuraDecorations";

export default function VaegtreguleringPage() {
  const steps = [
    {
      step: "01",
      title: "Indledende samtale & analyse",
      desc: "Vi kortlægger dine ønsker, nuværende spisevaner, daglige rytme og erfaringer.",
    },
    {
      step: "02",
      title: "Realistiske kostråd",
      desc: "Konkrete ændringer eller tilpasset kostplan, der passer til den hverdag, du faktisk har.",
    },
    {
      step: "03",
      title: "Valgfrit akupunktur-supplement",
      desc: "Japansk akupunktur eller ørenåle som supplement, hvis du ønsker at have det med.",
    },
    {
      step: "04",
      title: "Løbende opfølgning & justering",
      desc: "Vi taler om, hvad der har fungeret, og justerer undervejs frem for at kræve perfektion.",
    },
  ];

  const focusPoints = [
    {
      title: "Overblik over dine nuværende spisevaner",
      desc: "Vi undersøger måltider, pauser, indkøb og tid for at forstå mønstrene.",
      icon: "fa-solid fa-clipboard-check",
    },
    {
      title: "Konkrete kostråd eller en tilpasset kostplan",
      desc: "Enkle måltider med mere grønt, fuldkorn og gode proteinkilder uden besvær.",
      icon: "fa-solid fa-bowl-food",
    },
    {
      title: "Samtale om sukkertrang og måltidsrytme",
      desc: "Forståelse for, hvornår trangen opstår – uden konstante forbud og dårlig samvittighed.",
      icon: "fa-solid fa-heart-pulse",
    },
    {
      title: "Opfølgning og justering efter dine erfaringer",
      desc: "Løbende tilpasning så vanerne holder fast, også efter den første motivation.",
      icon: "fa-solid fa-arrows-rotate",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Vægtregulering med Akupunktur | Risskov</title>
        <meta
          name="description"
          content="Opnå naturlig vægtregulering med akupunktur og individuel kostvejledning i Risskov. Dæmp sukkertrang og få kroppen i sund balance. Læs mere her."
        />
        <meta property="og:title" content="Vægtregulering med Akupunktur | Risskov" />
        <meta
          property="og:description"
          content="Opnå naturlig vægtregulering med akupunktur og individuel kostvejledning i Risskov. Dæmp sukkertrang og få kroppen i sund balance. Læs mere her."
        />
        <link rel="canonical" href="https://kita-akupunktur.dk/vaegtregulering" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            "name": "Vægttab og vægtregulering i Risskov for dig fra Aarhus og Jystrup",
            "url": "https://kita-akupunktur.dk/vaegtregulering",
            "description":
              "Individuel kostvejledning og forløb med fokus på vaner og livsstilsændringer i Risskov nær Aarhus og Jystrup.",
            "provider": {
              "@type": "MedicalBusiness",
              "name": "Kita Akupunktur",
              "telephone": "+4529387186",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Kantorparken 11",
                "addressLocality": "Risskov",
                "postalCode": "8240",
                "addressRegion": "Aarhus",
                "addressCountry": "DK",
              },
            },
          })}
        </script>
      </Helmet>

      {/* Hero Header */}
      <section className="bg-gradient-to-b from-[#FFF5EE] via-[#FFFAF7] to-[#FFF5EE] py-14 lg:py-20 border-b border-[#ffe0cf]/80 relative z-20 overflow-hidden">
        {/* Botanical Cherry Branch Accent */}
        <div className="absolute -top-6 -right-6 w-56 sm:w-72 opacity-60 pointer-events-none z-10">
          <CherryBranchImg index={3} flip={true} className="w-full h-auto" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummesti" className="mb-4">
            <ol className="flex items-center space-x-2 text-xs sm:text-sm text-[#7a5b4f]">
              <li>
                <Link to="/" className="hover:text-[#d74814] transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <span className="text-[#ffe0cf] font-bold">/</span>
              </li>
              <li className="text-[#d74814] font-medium" aria-current="page">
                Vægtregulering & Vægttab
              </li>
            </ol>
          </nav>

          <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-3 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
            <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
            <span>KOSTVEJLEDNING & AKUPUNKTUR • RISSKOV VED AARHUS</span>
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-[#593427] max-w-4xl leading-tight">
            Vægttab og vægtregulering i Risskov for dig fra Aarhus og Jystrup
          </h1>

          <p className="text-base sm:text-lg text-[#7a5b4f] mt-4 max-w-3xl leading-relaxed">
            Ønsker du hjælp til vægttab, men savner en vej, der passer til din hverdag? Hos{" "}
            <Link to="/" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b3360b] transition-colors">
              Kita Akupunktur
            </Link>{" "}
            i Risskov tilbyder jeg individuel kostvejledning og forløb med fokus på dine vaner, også til dig fra Aarhus og Jystrup. Vi tager udgangspunkt i det, du gerne vil ændre, og finder små, konkrete skridt, som du kan arbejde videre med.
          </p>

          {/* Quick Info & CTA Badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4 mt-6 pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-[#d74814] text-white text-sm font-medium hover:bg-[#b83b0e] transition-colors shadow-xs"
            >
              <i className="fa-regular fa-calendar-check text-xs" aria-hidden="true" />
              <span>Kontakt & tidsbestilling</span>
            </Link>

            <a
              href="tel:+4529387186"
              className="inline-flex items-center space-x-2 px-4 py-2.5 rounded-xl bg-white/90 text-[#593427] text-sm font-medium hover:bg-white border border-[#ffe0cf] transition-colors shadow-2xs"
            >
              <i className="fa-solid fa-phone text-[#d74814] text-xs" aria-hidden="true" />
              <span>Tlf. 29 38 71 86</span>
            </a>

            <div className="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-[#e1ef9f]/30 border border-[#e1ef9f] text-xs text-[#593427]">
              <i className="fa-solid fa-square-parking text-[#593427]" aria-hidden="true" />
              <span>Gratis parkering foran huset</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16 space-y-16">
        
        {/* Section 1: Hjælp til vægttab for dig fra Aarhus og Jystrup */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Hjælp til vægttab for dig fra Aarhus og Jystrup
              </h2>
            </div>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Jeg hedder Mayumi Kita Thiele og er certificeret kostvejleder samt registreret alternativ behandler (RAB) inden for akupunktur. Hos mig begynder forløbet med en samtale om dine ønsker, erfaringer og udfordringer.{" "}
              <Link to="/om-mig" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e] transition-colors">
                Du kan læse mere om min baggrund og tilgang til behandling
              </Link>.
            </p>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Et vægttab kræver over tid, at du får mindre energi gennem mad og drikke, end kroppen bruger. Men vejen dertil behøver ikke være en streng kur. Vi ser på, hvilke ændringer der er realistiske for dig, så du ikke skal lave hele din hverdag om på én gang eller følge regler, der føles uoverskuelige.
            </p>

            {/* Quick bullets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div className="p-5 rounded-2xl bg-white/95 border border-[#ffe0cf] space-y-1.5 shadow-2xs">
                <h3 className="font-serif text-base font-bold text-[#593427] flex items-center">
                  <i className="fa-solid fa-certificate text-[#d74814] mr-2 text-sm" aria-hidden="true" />
                  Certificeret Kostvejleder
                </h3>
                <p className="text-xs sm:text-sm text-[#7a5b4f]">
                  Fagligt velfunderet vejledning med respekt for dine spisevaner og din hverdag.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-white/95 border border-[#ffe0cf] space-y-1.5 shadow-2xs">
                <h3 className="font-serif text-base font-bold text-[#593427] flex items-center">
                  <i className="fa-solid fa-award text-[#d74814] mr-2 text-sm" aria-hidden="true" />
                  RAB-registreret Akupunktør
                </h3>
                <p className="text-xs sm:text-sm text-[#7a5b4f]">
                  Godkendt behandler inden for klassisk japansk akupunktur og øreakupunktur.
                </p>
              </div>
            </div>
          </div>

          {/* Right Highlight Box */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-5 relative overflow-hidden">
              <div className="absolute -top-8 -right-8 w-44 opacity-15 pointer-events-none">
                <CherryBranchImg index={5} className="w-full h-auto" />
              </div>

              <div className="relative z-10 space-y-3">
                <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                  <SakuraFlower className="w-3.5 h-3.5" />
                  <span>Ingen strenge kure</span>
                </div>
                <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#593427]">
                  En vej der passer til din hverdag
                </h3>
                <p className="text-sm text-[#7a5b4f] leading-relaxed">
                  Hos Kita Akupunktur handler vægtregulering ikke om forbud eller uoverskuelige regler, men om at finde små, holdbare skridt, der skaber balance og trivsel på sigt.
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-[#593427] text-white flex items-start space-x-4 relative z-10 shadow-md">
                <div className="w-8 h-8 rounded-full bg-[#e1ef9f] text-[#593427] flex items-center justify-center shrink-0 mt-0.5 font-bold">
                  <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                </div>
                <div>
                  <h4 className="font-serif font-bold text-base text-[#ffe0cf]">Individuel samtale først</h4>
                  <p className="text-xs sm:text-sm text-stone-200 mt-0.5">
                    Forløbet begynder altid med en grundig samtale om dine ønsker, vaner og udfordringer.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Kostvejledning i Risskov, også for dig fra Jystrup */}
        <section className="bg-white/90 p-8 sm:p-10 lg:p-12 rounded-3xl border border-[#ffe0cf] shadow-2xs space-y-8 relative overflow-hidden">
          <div className="max-w-3xl space-y-4">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>INDIVIDUEL KOSTRÅDGIVNING</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Kostvejledning i Risskov, også for dig fra Jystrup
            </h2>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Søger du kostvejledning i Aarhus, eller bor du i Jystrup, er min klinik i Risskov en mulighed for personlig støtte. Vi gennemgår dine spisevaner og taler om måltider, sult og mæthed. Det giver et udgangspunkt for konkrete kostråd eller en kostplan, som tager højde for dine præferencer og den hverdag, du faktisk har.
            </p>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Kostvejledning handler ikke kun om, hvad der ligger på tallerkenen. Tid, indkøb og pauser har også betydning for dine valg. Vi kan derfor arbejde med enkle måltider, som er nemme at få lavet, og se på, hvordan du får mere grønt, fuldkorn og gode proteinkilder ind i kosten uden at gøre den besværlig.
            </p>
          </div>

          {/* 4 List Points as Elegant Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-2">
            {focusPoints.map((item, idx) => (
              <div
                key={idx}
                className="bg-[#FFFDFB] p-5 rounded-2xl border border-[#ffe0cf] space-y-3 shadow-2xs hover:border-[#d74814]/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-[#ffe0cf]/60 border border-[#ffe0cf] flex items-center justify-center text-[#d74814]">
                  <i className={`${item.icon} text-base`} aria-hidden="true" />
                </div>
                <h3 className="font-serif font-bold text-base text-[#593427]">
                  {item.title}
                </h3>
                <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3: Når sukkertrang gør det svært at holde nye vaner */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="glass-card p-6 sm:p-8 rounded-3xl border border-[#ffe0cf] shadow-md space-y-4 relative overflow-hidden bg-gradient-to-br from-[#FFFAF7] to-[#FFE0CF]/30">
              <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold text-[#d74814]">
                <SakuraFlower className="w-3.5 h-3.5" />
                <span>Forstå mønstrene</span>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#593427]">
                Ingen dårlig samvittighed
              </h3>
              <p className="text-sm text-[#7a5b4f] leading-relaxed">
                Når sukkertrangen rammer, handler det sjældent om manglende viljestyrke, men om kroppens signaler efter en travl dag eller for få måltider. Målet er en balance, hvor der også er plads til nydelse.
              </p>
              <div className="pt-2">
                <div className="p-4 rounded-xl bg-white/90 border border-[#ffe0cf] flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-full bg-[#e1ef9f] text-[#593427] flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-spa text-xs" aria-hidden="true" />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-[#593427]">
                    Trygge rammer med fokus på varige vaner
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-6 order-1 lg:order-2">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                <SakuraFlower className="w-4 h-4" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                Når sukkertrang gør det svært at holde nye vaner
              </h2>
            </div>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Sukkertrang kan hænge sammen med både sult, vaner, følelser og bestemte situationer. Derfor ser vi på, hvornår trangen opstår hos dig. Er det efter en lang arbejdsdag, foran fjernsynet eller på dage med få måltider? Det handler om at forstå mønstret, ikke om at finde fejl ved din viljestyrke.
            </p>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Ud fra dine erfaringer finder vi små ændringer, du kan prøve af. Det kan være mere mættende måltider eller et planlagt mellemmåltid, hvis der går længe mellem måltiderne. Der kan også være plads til søde sager. Målet er at finde en måde at spise på, som ikke bygger på konstante forbud og dårlig samvittighed.
            </p>
          </div>
        </section>

        {/* Section 4: Hvilken rolle har akupunktur ved vægtregulering? */}
        <section className="glass-sakura p-8 sm:p-10 rounded-3xl border border-[#ffe0cf] space-y-6 relative overflow-hidden">
          <div className="absolute -top-8 -right-8 w-44 opacity-15 pointer-events-none">
            <CherryBranchImg index={1} className="w-full h-auto" />
          </div>

          <div className="relative z-10 max-w-3xl space-y-3">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>SUPPLEMENT TIL KOSTVEJLEDNING</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Hvilken rolle har akupunktur ved vægtregulering?
            </h2>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Hos Kita Akupunktur kan{" "}
              <Link to="/japansk-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e] transition-colors">
                japansk akupunktur
              </Link>{" "}
              og{" "}
              <Link to="/oere-akupunktur" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e] transition-colors">
                øreakupunktur
              </Link>{" "}
              indgå som supplement i et individuelt forløb. Vi taler om, hvad behandlingen indebærer, og om du ønsker den som en del af dit forløb. Jeg tilpasser behandlingen efter din sensitivitet og dine reaktioner og bruger tynde, sterile engangsnåle.
            </p>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Der er ikke sikker dokumentation for, at akupunktur i sig selv giver et varigt vægttab eller mindsker sukkertrang. Derfor er kostvejledning og arbejdet med dine vaner centrale dele af forløbet. Akupunktur er et muligt supplement og erstatter ikke lægelig vurdering eller behandling, når der er behov for det.
            </p>
          </div>

          {/* Clarity Boxes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 relative z-10">
            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#d74814]">
                <i className="fa-solid fa-utensils text-sm" aria-hidden="true" />
                <h3 className="font-serif font-bold text-base text-[#593427]">Hovedfokus: Kostvejledning & Vaner</h3>
              </div>
              <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                Arbejdet med dine vaner, måltidsrytmer og sunde valg er kernen i et holdbart og varigt vægttab.
              </p>
            </div>

            <div className="bg-white/95 p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
              <div className="flex items-center space-x-2 text-[#d74814]">
                <i className="fa-solid fa-spa text-sm" aria-hidden="true" />
                <h3 className="font-serif font-bold text-base text-[#593427]">Supplement: Japansk Akupunktur & Ørenåle</h3>
              </div>
              <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">
                Et frivilligt supplement med tynde, sterile engangsnåle, der tilpasses nænsomt efter dit ønske.
              </p>
            </div>
          </div>
        </section>

        {/* Section 5 & 6: Sådan følger vi dit vægttab og dine nye vaner + Hvor langt skal et forløb være? */}
        <section className="bg-white/95 p-8 sm:p-10 lg:p-12 rounded-3xl border border-[#ffe0cf] shadow-2xs space-y-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                  <SakuraFlower className="w-4 h-4" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Sådan følger vi dit vægttab og dine nye vaner
                </h2>
              </div>
              <p className="text-[#593427]/90 leading-relaxed text-base">
                Ved opfølgningen taler vi om, hvad der har fungeret, og hvad der har været svært. Måske skal et kostråd gøres mere enkelt, eller en ændring passe bedre til dine arbejdstider. Vægtregulering handler også om at kunne holde fast efter den første motivation, så vi justerer undervejs frem for at kræve perfektion.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-full bg-[#ffe0cf] border border-[#ffe0cf] flex items-center justify-center text-[#d74814] shrink-0">
                  <SakuraFlower className="w-4 h-4" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
                  Hvor langt skal et forløb med vægtregulering være?
                </h2>
              </div>
              <p className="text-[#593427]/90 leading-relaxed text-base">
                Der er ikke et fast antal samtaler eller behandlinger, som passer til alle. Omfanget afhænger af dine mål og behov og aftales individuelt. Vi ser både på vægtens udvikling og på dine erfaringer med måltiderne. Hvis din vægt ændrer sig uden en klar grund, bør du få det vurderet hos din læge.
              </p>
            </div>
          </div>

          {/* Steps Grid */}
          <div className="pt-2 space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#593427] flex items-center">
              <SakuraFlower className="w-4 h-4 text-[#d74814] mr-2" />
              Forløbets trin hos Kita Akupunktur
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {steps.map((item, idx) => (
                <div key={idx} className="bg-[#FFFDFB] p-5 rounded-2xl border border-[#ffe0cf] space-y-2 shadow-2xs">
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-[#e1ef9f] text-[#593427] text-xs font-bold">
                    Trin {item.step}
                  </span>
                  <h4 className="text-base font-bold text-[#593427]">{item.title}</h4>
                  <p className="text-xs sm:text-sm text-[#7a5b4f] leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 7: Vil du have hjælp til vægttab og vægtregulering? */}
        <section className="glass-card p-8 sm:p-10 lg:p-12 rounded-3xl border border-[#ffe0cf] shadow-md relative overflow-hidden bg-gradient-to-br from-[#FFF5EE] via-[#FFFAF7] to-[#FFE0CF]/30">
          <div className="absolute -top-10 -right-10 w-48 opacity-20 pointer-events-none">
            <CherryBranchImg index={4} className="w-full h-auto" />
          </div>

          <div className="max-w-3xl space-y-5 relative z-10">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>START DIT FORLØB I RISSKOV</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Vil du have hjælp til vægttab og vægtregulering?
            </h2>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Leder du efter hjælp til vægttab i Aarhus eller støtte til vægtregulering i Aarhus, er du velkommen hos Kita Akupunktur, også hvis du kommer fra Jystrup. Klinikken ligger på Kantorparken 11, 8240 Risskov, med gratis parkering foran huset. Her kan vi tale om dine ønsker og afklare, hvordan et forløb kan tilrettelægges.
            </p>

            <p className="text-[#593427]/90 leading-relaxed text-base sm:text-lg">
              Du behøver ikke have en færdig plan, før du henvender dig. Ring på{" "}
              <a href="tel:+4529387186" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e] transition-colors">
                29 38 71 86
              </a>
              , hvis du har spørgsmål om kostvejledning eller kombinationen med akupunktur. Du kan også gå til{" "}
              <Link to="/kontakt" className="text-[#d74814] font-semibold underline decoration-[#ffe0cf] hover:text-[#b83b0e] transition-colors">
                kontakt og tidsbestilling
              </Link>
              . Det første skridt er en samtale om, hvad du ønsker hjælp til.
            </p>
          </div>
        </section>

        {/* Related Treatments Navigation */}
        <section className="p-6 rounded-2xl bg-[#fff5ee] border border-[#ffe0cf] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="space-y-1 text-center sm:text-left">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#d74814]">
              Relaterede behandlingsområder
            </div>
            <p className="text-sm text-[#593427] font-serif">
              Kroppens balance, stressniveau og nervesystem hænger tæt sammen
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5">
            <Link
              to="/oere-akupunktur"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Øreakupunktur →
            </Link>
            <Link
              to="/rygestop-behandling"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Rygestop behandling →
            </Link>
            <Link
              to="/stress"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Stress & uro →
            </Link>
            <Link
              to="/japansk-akupunktur"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Japansk akupunktur →
            </Link>
            <Link
              to="/akupunktur"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Klassisk akupunktur →
            </Link>
            <Link
              to="/om-mig"
              className="px-3.5 py-2 rounded-lg bg-white border border-[#ffe0cf] text-xs font-medium text-[#593427] hover:text-[#d74814] hover:border-[#d74814] transition-colors"
            >
              Om Mayumi Kita Thiele →
            </Link>
          </div>
        </section>

        {/* Hurtige genveje */}
        <div className="flex flex-wrap items-center justify-center gap-3 pt-4 text-xs sm:text-sm">
          <Link
            to="/priser"
            className="px-4 py-2 rounded-full bg-[#d74814] text-white font-medium hover:bg-[#593427] transition-colors shadow-2xs"
          >
            Se priser & tilskud fra “danmark”
          </Link>
          <Link
            to="/om-mig"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Læs om Mayumis kostvejlederuddannelse
          </Link>
          <Link
            to="/faq"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Samlet FAQ
          </Link>
          <Link
            to="/kontakt"
            className="px-4 py-2 rounded-full bg-white text-[#593427] border border-[#ffe0cf] font-medium hover:bg-[#ffe0cf]/40 transition-colors"
          >
            Bestil tid i Risskov
          </Link>
        </div>

        {/* Contact and Booking Section with Form */}
        <section className="pt-4">
          <div className="text-center max-w-2xl mx-auto mb-8 space-y-2">
            <div className="inline-flex items-center space-x-2 px-3 py-1 rounded-full glass-pill mb-1 text-xs font-semibold text-[#d74814] border border-[#ffe0cf]">
              <SakuraFlower className="w-3.5 h-3.5 text-[#d74814]" />
              <span>BESTIL TID ELLER STIL ET SPØRGSMÅL</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-serif text-[#593427]">
              Kontakt & tidsbestilling
            </h2>
            <p className="text-sm sm:text-base text-[#7a5b4f]">
              Klinikken ligger i rolige rammer på Kantorparken 11 i Risskov nær Aarhus med gratis parkering lige ved døren.
            </p>
          </div>
          <ContactForm />
        </section>

        {/* FAQ Section sidst på siden */}
        <section className="pt-8 pb-12 border-t border-[#ffe0cf]/80">
          <FaqSection
            title="Ofte stillede spørgsmål om vægtregulering"
            subtitle="Få svar på hvordan akupunktur dæmper sukkertrang, stabiliserer blodsukkeret og støtter et varigt vægttab i harmoni med kroppen."
            faqs={[
              {
                question: "Hvordan kan akupunktur støtte et varigt vægttab og en sundere livsstil?",
                answer:
                  "Akupunktur er et yderst effektivt fysiologisk støtteredskab, der afbalancerer kroppen indefra. Ved at stimulere specifikke meridianpunkter for mave, milt og fordøjelse stabiliseres blodsukkeret, og kroppens naturlige mæthedssignaler genoprettes. Samtidig dæmpes den indre stress og uro, som ofte er hovedårsagen til følelsesmæssig overspisning og uimodståelige cravings.",
              },
              {
                question: "Hvilken rolle spiller øreakupunktur mod sukkertrang og appetitregulering?",
                answer: (
                  <>
                    I{" "}
                    <Link to="/oere-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      øreakupunktur
                    </Link>{" "}
                    findes præcise reflekspunkter for 'sult', 'mave' og det beroligende punkt 'Shen Men'. Ved at stimulere disse zoner – suppleret med diskrete små magnetkugler eller permanåle på et lille plaster, som bliver siddende i øret i op til en uge – dæmpes den akutte trang til søde sager og snacks markant, så du nemt kan træffe sunde valg.
                  </>
                ),
              },
              {
                question: "Får jeg også individuel kostvejledning som en del af forløbet?",
                answer: (
                  <>
                    Ja, som certificeret kostvejleder fra At Work Skolen (læs mere under{" "}
                    <Link to="/om-mig" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      om mig
                    </Link>
                    ) kombinerer jeg nålebehandling med realistisk og let overskuelig kostvejledning. Vi benytter ingen ekstreme, restriktive kure, men fokuserer på velsmagende principper om blodsukkerstabilitet og velfungerende fordøjelse.
                  </>
                ),
              },
              {
                question: "Hvor mange behandlinger skal der til, og hvor ofte skal jeg komme?",
                answer: (
                  <>
                    Et typisk forløb starter med 1 ugentlig session i de første 4-6 uger for at bryde de indgroede sukkervaner, dæmpe appetitten og kickstarte et stabilt vægttab. Herefter trappes intervallet ned. Læs mere om forløbspriser og tilskudsmuligheder under{" "}
                    <Link to="/priser" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      priser og tilskud
                    </Link>
                    .
                  </>
                ),
              },
              {
                question: "Kan akupunktur hjælpe, hvis overvægten skyldes hormonelle ubalancer eller overgangsalder?",
                answer: (
                  <>
                    Ja, i høj grad. Mange kvinder oplever, at vægten stiger i overgangsalderen, eller at fedtforbrændingen bremses af hormonelle udsving, væskeophobninger og forstyrret søvn. Gennem{" "}
                    <Link to="/japansk-akupunktur" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      japansk akupunktur
                    </Link>{" "}
                    og regulering af{" "}
                    <Link to="/stress" className="text-[#d74814] font-medium underline hover:text-[#593427]">
                      stressniveauet
                    </Link>{" "}
                    styrker vi organmeridianerne, stimulerer væskebalancen og beroliger hormonsystemet.
                  </>
                ),
              },
            ]}
          />
        </section>

      </div>
    </>
  );
}
